import {
  formatAddress,
  formatContact,
  formatHumanName,
  formatReferenceString,
  getAccountInviteDate,
  getIdentifier,
} from "@/utils/fhir-utils";
import { HCN_TYPE_CODE, MRN_TYPE_CODE } from "@/utils/naming-system";

import { fhirR4 } from "@smile-cdr/fhirts";

/**
 * Gonna be deprecated soon.
 */
/**
 * Extract all the information from a FHIR Patient resource.
 * @param patient - The FHIR Patient resource.
 * @returns The extracted information.
 */
export const extractInfoFromPatient = (patient: fhirR4.Patient) => {
  const id = patient?.id ?? "";
  const nameFull = formatHumanName(patient?.name?.[0] as fhirR4.HumanName, {
    lineSeparator: " ",
  });
  const nameFamilyGiven = formatHumanName(patient?.name?.[0] as fhirR4.HumanName, {
    include: ["family", "given"],
  });
  const nameGivenFamily = formatHumanName(patient?.name?.[0] as fhirR4.HumanName, {
    include: ["given", "family"],
    lineSeparator: " ",
  });
  const nameFirst = formatHumanName(patient?.name?.[0] as fhirR4.HumanName, {
    include: ["first"],
    includeTrailingWhitespace: true,
  });
  const nameMiddle = formatHumanName(patient?.name?.[0] as fhirR4.HumanName, {
    include: ["middle"],
    includeTrailingWhitespace: true,
  });
  const nameFamily = formatHumanName(patient?.name?.[0] as fhirR4.HumanName, {
    include: ["family"],
    includeTrailingWhitespace: true,
  });
  const mrn = getIdentifier(patient?.identifier, MRN_TYPE_CODE);
  const hcn = getIdentifier(patient?.identifier, HCN_TYPE_CODE);
  const clinic = formatReferenceString(patient?.managingOrganization as fhirR4.Reference);
  const mrp = formatReferenceString(patient?.generalPractitioner?.[0] as fhirR4.Reference);

  // NOTE: If the patient does not have the `status` field defined, we should assume the patient is active.
  const status = patient?.active === undefined || patient?.active ? "Active" : "Inactive";

  const birthDate = patient?.birthDate as fhirR4.Date;
  const patientContact = patient?.telecom as fhirR4.ContactPoint[];
  // Don't specify rank for emails as they often don't have rank in FHIR data
  const email = patientContact && formatContact(patientContact, "email");
  const email2 = patientContact && formatContact(patientContact, "email", 2);
  const phone1 = patientContact && formatContact(patientContact, "phone", 1);
  const phone2 = patientContact && formatContact(patientContact, "phone", 2);
  const patientAddress = patient?.address?.[0] as fhirR4.Address;
  const fullAddress = patientAddress && formatAddress(patientAddress);
  const addressLine1 = patientAddress && formatAddress(patientAddress, { include: ["line"] });
  const addressLine2 = patientAddress && formatAddress(patientAddress, { include: ["city", "state", "postalCode"] });
  const gender = patient?.gender;
  const accountInviteDate = getAccountInviteDate(patient);

  return {
    id,
    nameFull,
    nameFamilyGiven,
    nameGivenFamily,
    nameFirst,
    nameMiddle,
    nameFamily,
    mrn,
    hcn,
    clinic,
    mrp,
    status,
    birthDate,
    patientContact,
    email,
    email2,
    phone1,
    phone2,
    patientAddress,
    fullAddress,
    addressLine1,
    addressLine2,
    gender,
    accountInviteDate,
  };
};

/**
 * Utility function to get initials from a name
 * @param name Name to get initials from
 * @param count Number of initials to return
 * @returns Initials of the name
 */
export const getNameInitials = (name: string, count = 2) => {
  const initials = name
    .split(" ")
    .map(word => word.charAt(0))
    .join("")
    .toUpperCase();

  return initials.slice(0, count);
};

/**
 * Converts a base64-encoded image string (from localStorage/sessionStorage) to a File object.
 * Remembers the original file type if present in the data URL, otherwise defaults to 'image/png'.
 * @param imageBase64 The base64 string (data URL) to convert.
 * @param filename The filename for the resulting File (default: 'unsent-image').
 * @returns File object or null if conversion fails.
 */
export const base64ToImageFile = (imageBase64: string, filename = "unsent-image"): File | null => {
  try {
    const arr = imageBase64.split(",");
    const match = arr[0].match(/data:(.*?);base64/);
    const mime = match ? match[1] : "application/octet-stream";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    // Try to get extension from mime type
    let ext = "";
    if (mime && mime.includes("/")) {
      ext = "." + mime.split("/")[1];
    }
    return new File([u8arr], filename + ext, { type: mime });
  } catch {
    return null;
  }
};

/**
 * Utility function to return a filtered version of a string based on the specified format.
 * @param str The string to return a filtered version from.
 * @param format The format to filter the string by. Can be 'alpha', 'alphaNoSpaces', 'numeric', 'alphaNumeric', 'email', or 'phone'.
 *             - 'alpha': Only alphabetic characters and spaces are allowed.
 *             - 'alphaNoSpaces': Only alphabetic characters are allowed, no spaces.
 *             - 'numeric': Only numeric characters are allowed.
 *             - 'alphaNumeric': Only alphanumeric characters and spaces are allowed.
 *             - 'email': Allows alphanumeric characters, dots, underscores, and hyphens
 *             - 'phone': Allows digits, spaces, parentheses, and hyphens. Also removes multiple consecutive spaces and trims leading/trailing spaces.
 * @returns The filtered string based on the specified format.
 */
export const getFilteredStringWithFormat = (
  str: string,
  format: "alpha" | "alphaNoSpaces" | "numeric" | "alphaNumeric" | "email" | "phone"
): string => {
  if (format === "alpha") {
    return str.replace(/[^a-zA-Z\s]/g, "");
  } else if (format === "alphaNoSpaces") {
    return str.replace(/[^a-zA-Z]/g, "");
  } else if (format === "numeric") {
    return str.replace(/[^0-9]/g, "");
  } else if (format === "alphaNumeric") {
    return str.replace(/[^a-zA-Z0-9\s]/g, "");
  } else if (format === "email") {
    // Allow alphanumeric characters, dots, underscores, and hyphens in the local part,
    // and alphanumeric characters and dots in the domain part.
    str = str.replace(/[^a-zA-Z0-9._%+-@]/g, "");
    // Ensure there's only one '@' character
    const parts = str.split("@");
    if (parts.length > 2) {
      str = parts.slice(0, -1).join("@") + "@" + parts[parts.length - 1];
    }
  } else if (format === "phone") {
    // Allow only digits, spaces, parentheses, and hyphens
    str = str.replace(/[^0-9\s()-]/g, "");
    // Remove multiple consecutive spaces
    str = str.replace(/\s+/g, " ");
    // Remove leading and trailing spaces
    str = str.trim();
  }
  return str;
};

/**
 * Utility function to get initials from a name
 * @param str The string to capitalize.
 * @returns The capitalized string, or an empty string if the input is falsy.
 */
export const capitalize = (str: string): string => {
  if (!str) {
    return "";
  }
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Utility function that only trims leading whitespace from a string.
 * @param str The string to remove leading whitespace.
 * @returns The string with removed leading whitespace.
 */
export const trimLeadingWhitespace = (str: string): string => {
  return str.replace(/^\s+/, "");
};
